import React, { useCallback, useMemo } from "react";
import { AgentState } from "../Models/AgentState";
import { useStore } from "utils/useStore";
import {
    Divider,
    Grid,
    Grow,
    IconButton,
    Link,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { ActiveDomain } from "stores/RootStore";
import AcxMainTextField from "components/UI/AcxMainTextFieldGrid";
import PaperAirplaneSvg from "SvgIcons/PaperAirplaneSvg";
import { StopCircleRounded } from "@mui/icons-material";
import { sxClsx } from "utils/styleClsx";
import { SwitchContext } from "./SwitchContext";
import { observer } from "mobx-react";
import AgentStore from "../Stores/AgentStore";
import { resolveDomainFromLocation } from "shared/functions/resolveDomainFromLocation";
import { useCurrentChatStore } from "../Stores/useCurrentChat";

export const ConversationInput = observer(
    ({ fullscreen }: { fullscreen?: boolean }) => {
        const currentChatStore = useCurrentChatStore();

        const agentStore = useStore(AgentStore);
        // Locked until the KBo User Agreement is accepted.
        const isLocked = agentStore.isKboInteractionLocked;

        const submit = useCallback(async () => {
            if (isLocked) return;
            if (currentChatStore.state !== AgentState.Idle) return;
            if (
                !currentChatStore.currentInput ||
                currentChatStore.currentInput.trim() === ""
            )
                return;
            await currentChatStore.streamingSubmitInput();
        }, [currentChatStore, isLocked]);

        const isViewingConversationsOrSignals = useMemo(() => {
            const activeDomain = agentStore.rootStore.activeLocation.domain;
            if (!activeDomain) return false;
            return (
                activeDomain === ActiveDomain.Conversations ||
                activeDomain === ActiveDomain.Signals
            );
        }, [agentStore.rootStore.activeLocation.domain]);

        const hasChartDataPointContext =
            currentChatStore.hasChartDataPointContext;

        const wasViewingConversationsOrSignals = useMemo(() => {
            if (
                !agentStore.routeBeforeFullscreen ||
                agentStore.routeBeforeFullscreen.trim() === ""
            )
                return false;
            const previousDomain = resolveDomainFromLocation(
                agentStore.routeBeforeFullscreen,
            );
            return (
                previousDomain === ActiveDomain.Conversations ||
                previousDomain === ActiveDomain.Signals
            );
        }, [agentStore.routeBeforeFullscreen]);

        const theme = useTheme();

        const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

        const endAdornment =
            currentChatStore.state === AgentState.Idle ? (
                <PaperAirplaneSvg
                    style={{
                        margin: "8px",
                        marginLeft: "4px",
                        cursor: currentChatStore.currentInput
                            ? "pointer"
                            : "default",
                    }}
                    onClick={submit}
                    id="kbo-send-chat"
                />
            ) : (
                <IconButton onClick={() => currentChatStore.abort()}>
                    <StopCircleRounded />
                </IconButton>
            );

        return (
            <React.Fragment>
                {!fullscreen && (
                    <Grid item>
                        <Divider />
                    </Grid>
                )}
                <Grow
                    in={true}
                    timeout={700}
                    style={{ transformOrigin: "center" }}
                >
                    <Grid
                        container
                        item
                        direction="column"
                        sx={sxClsx(
                            { bottom: 0 },
                            fullscreen && {
                                paddingX: isLargeScreen ? 20 : 4,
                                marginTop: 4,
                            },
                        )}
                        alignSelf="center"
                        className={"pendo-ignore"}
                    >
                        <Grid
                            container
                            item
                            paddingInline={2}
                            paddingBlock={2}
                            direction="column"
                            rowGap={1}
                            sx={sxClsx(
                                fullscreen && {
                                    border: `1px solid ${theme.palette.gray[400]}`,
                                    borderRadius: 1,
                                    paddingTop:
                                        fullscreen &&
                                        wasViewingConversationsOrSignals
                                            ? 1
                                            : 0,
                                    paddingX: 0,
                                },
                            )}
                        >
                            {(isViewingConversationsOrSignals ||
                                (fullscreen &&
                                    wasViewingConversationsOrSignals) ||
                                hasChartDataPointContext) && (
                                <Grid item sx={sxClsx(fullscreen && { px: 1 })}>
                                    <SwitchContext />
                                </Grid>
                            )}
                            <Grid item width="100%">
                                <AcxMainTextField
                                    value={currentChatStore.currentInput}
                                    onChange={(event) => {
                                        currentChatStore.onChangeHandler(
                                            event.target.value,
                                        );
                                    }}
                                    id="agent-chat-user-input"
                                    placeholderText={
                                        isLocked
                                            ? "Accept the user agreement to start chatting"
                                            : "What can I help you with?"
                                    }
                                    disabled={isLocked}
                                    startAdornment={
                                        isLocked ? (
                                            <LockOutlinedIcon
                                                id="kbo-chat-locked"
                                                sx={{
                                                    fontSize: "18px",
                                                    marginLeft: 1,
                                                    color: theme.palette
                                                        .gray[400],
                                                }}
                                            />
                                        ) : undefined
                                    }
                                    endAdornment={
                                        isLocked ? undefined : endAdornment
                                    }
                                    textContainerSx={sxClsx(
                                        !fullscreen && {
                                            boxShadow:
                                                "0px 0px 8px 0px rgba(0, 0, 0, 0.10)",
                                            ":focus-within": {
                                                borderColor:
                                                    theme.palette.gray[300],
                                            },
                                        },
                                        fullscreen && {
                                            border: "none",
                                        },
                                    )}
                                    inputProps={{
                                        style: {
                                            fontFamily: "Inter",
                                            fontSize: "16px",
                                            fontStyle: "normal",
                                            fontWeight: 600,
                                            lineHeight: "24px",
                                            color: theme.palette.gray[500],
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                submit();
                                            }
                                        },
                                    }}
                                    autoComplete="off"
                                    autoFocus
                                    multiLine={fullscreen}
                                    rowsMax={fullscreen ? 6 : 1}
                                />
                            </Grid>
                            {isLocked &&
                                agentStore.userAgreementStore.isCollapsed && (
                                    <Grid
                                        item
                                        sx={sxClsx(fullscreen && { px: 1 })}
                                    >
                                        <Link
                                            component="button"
                                            id="kbo-chat-review-agreement"
                                            onClick={() =>
                                                agentStore.showUserAgreement()
                                            }
                                            sx={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                textDecoration: "none",
                                                ":hover": {
                                                    textDecoration: "underline",
                                                },
                                            }}
                                        >
                                            Review agreement
                                        </Link>
                                    </Grid>
                                )}
                        </Grid>
                    </Grid>
                </Grow>
            </React.Fragment>
        );
    },
);
